/**
 * WorkflowOverflowMenu — the `⋮` button shown next to the Publish button
 * in the workflow sub-header.
 *
 * Mirrors the actions the old `DraftStatusMenu` exposed on the floating
 * dock: switch between draft and published view, trigger validation,
 * discard the current session, or discard the entire draft.
 *
 * Reads draft state directly from `useDraftModeStore` because this menu
 * is rendered above the `DraftModeProvider` in the tree — the store
 * stays in sync with context, so the menu reflects the right mode
 * regardless of where it lives.
 */

import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  MenuDivider,
  Tooltip,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  MoreVertical20Regular,
  ArrowSwap20Regular,
  ArrowReset20Regular,
  CheckmarkCircle20Regular,
  Delete20Regular,
  PlugConnected20Regular,
} from '@fluentui/react-icons';
import { useCallback, useMemo, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useDraftModeStore, selectIsDraftMode } from '@/stores/draftModeStore';
import { useSessionHistoryStore } from '@/stores/sessionHistoryStore';
import { useWorkflowStore } from '@/stores/workflowStore';
import { useConnectionStore } from '@/stores/connectionStore';
import type { ConnectionsConfiguration } from '@/types/logicApps';
import { useDraftDiscardActions } from '@/hooks/useDraftDiscardActions';
import { useGuardedModeSwitch } from '@/hooks/useGuardedModeSwitch';
import { telemetryService } from '@/services/telemetryService';
import { ConnectionsDialog } from './ConnectionsDialog';

/**
 * Confirmation copy for the two destructive discard actions. Centralised
 * so the dialog stays in lockstep with the menu items — if we ever add a
 * third discard variant (e.g. "discard parameters") it slots in here.
 */
type DiscardConfirmKind = 'session' | 'draft';
interface DiscardConfirmCopy {
  title: string;
  description: string;
  confirmLabel: string;
  busyLabel: string;
  /** Telemetry event fired the moment the user clicks the confirm button. */
  telemetry: string;
}
const DISCARD_CONFIRM_COPY: Record<DiscardConfirmKind, DiscardConfirmCopy> = {
  session: {
    title: 'Discard session changes?',
    description:
      'This reverts the canvas to the state it was in when you started this session. Any edits you’ve made since will be lost. This cannot be undone.',
    confirmLabel: 'Discard session',
    busyLabel: 'Discarding…',
    telemetry: 'workflow.draft.session_discard_confirmed',
  },
  draft: {
    title: 'Discard draft?',
    description:
      'This replaces the draft with the currently published version. All unpublished changes will be lost. This cannot be undone.',
    confirmLabel: 'Discard draft',
    busyLabel: 'Discarding…',
    telemetry: 'workflow.draft.discard_confirmed',
  },
};

export interface WorkflowOverflowMenuProps {
  /** Trigger proactive validation. When omitted, the Validate item is hidden. */
  onValidate?: () => void;
  /** Current validation error count — surfaced in the menu label when > 0. */
  validationErrorCount?: number;
  /** Whether validation is currently running. */
  isValidating?: boolean;
  /**
   * URL workflow name. Required for the discard actions to persist to the
   * backend; when omitted the discards stay local-only.
   */
  workflowName?: string;
}

/**
 * True iff `a` and `b` are both defined and differ in content. Ref-identity
 * short-circuit avoids the deep walk on the common steady-state path.
 *
 * Biased toward "no diff" when either side is missing — used for the
 * workflow comparison where the prod/session baseline must exist for a
 * discard to even make sense.
 */
const hasContentDiff = <T,>(a: T | null | undefined, b: T | null | undefined): boolean => {
  if (!a || !b) return false;
  if (a === b) return false;
  return !isEqual(a, b);
};

/**
 * Diff for artifacts where `null`/`undefined` is a legitimate baseline
 * (connections / parameters / notes can all legitimately be absent on one
 * side). Treats null and undefined as equivalent; one side having content
 * while the other is empty is itself a discardable change.
 */
const hasArtifactDiff = (live: unknown, baseline: unknown): boolean => {
  const a = live ?? null;
  const b = baseline ?? null;
  if (a === b) return false;
  return !isEqual(a, b);
};

/**
 * `exportToConnectionsConfig` always returns a 5-section object even when
 * the connection store has zero references — meaning a freshly initialized
 * store with no edits would deep-diff as "different" from a `null`
 * baseline. Collapse that empty-shell case down to `null` so the diff is
 * driven by actual user-visible connection state.
 */
const normalizeConnections = (
  c: ConnectionsConfiguration | null | undefined,
): ConnectionsConfiguration | null => {
  if (!c) return null;
  const hasAny =
    Object.keys(c.managedApiConnections ?? {}).length > 0 ||
    Object.keys(c.serviceProviderConnections ?? {}).length > 0 ||
    Object.keys(c.agentConnections ?? {}).length > 0 ||
    Object.keys(c.agentMcpConnections ?? {}).length > 0;
  return hasAny ? c : null;
};

const useStyles = makeStyles({
  destructiveMenuItem: {
    color: tokens.colorPaletteRedForeground1,
    ':hover': {
      color: tokens.colorPaletteRedForeground1,
      backgroundColor: tokens.colorPaletteRedBackground1,
    },
    // Fluent's MenuItem applies disabled colors via [aria-disabled="true"]
    // but our red `color` override above wins via specificity. Restore the
    // disabled foreground here so users can tell when the action isn't
    // available (otherwise a disabled discard still looks clickable).
    '&[aria-disabled="true"]': {
      color: tokens.colorNeutralForegroundDisabled,
      ':hover': {
        color: tokens.colorNeutralForegroundDisabled,
        backgroundColor: 'transparent',
      },
    },
  },
  // Primary button in the discard confirm dialog. Fluent's `primary`
  // appearance defaults to the brand color; override to the danger palette
  // so the destructive choice reads as such (matches the red menu item).
  // Griffel forbids the `borderColor` shorthand, so we only override the
  // background — the brand-button border is hidden behind the fill anyway.
  destructiveConfirmButton: {
    backgroundColor: tokens.colorPaletteRedBackground3,
    ':hover': {
      backgroundColor: tokens.colorPaletteRedForeground1,
    },
    ':hover:active': {
      backgroundColor: tokens.colorPaletteRedForeground3,
    },
  },
});

function buildValidateLabel(
  isValidating: boolean,
  validationErrorCount: number | undefined,
): string {
  if (isValidating) return 'Validating...';
  if (validationErrorCount !== undefined && validationErrorCount > 0) {
    const plural = validationErrorCount !== 1 ? 's' : '';
    return `Validate workflow (${validationErrorCount} error${plural})`;
  }
  return 'Validate workflow';
}

export const WorkflowOverflowMenu = ({
  onValidate,
  validationErrorCount,
  isValidating = false,
  workflowName,
}: WorkflowOverflowMenuProps) => {
  const styles = useStyles();
  const isDraftMode = useDraftModeStore(selectIsDraftMode);
  // Use the shared discard hook so the menu runs the same full sequence
  // (canvas refresh + dirty-state reset + backend persist) as
  // `DraftModeContext`. The menu sits above `DraftModeProvider` in the
  // tree, so it can't consume the context directly — going through the
  // hook keeps both call sites in lockstep.
  const { discardDraft, discardSession } = useDraftDiscardActions({
    ...(workflowName && { workflowName }),
  });
  // Both discard checks are derived from four artifact pairs: workflow,
  // connections, parameters, and notes. Discard actions reset all of them,
  // so the menu items need to enable when *any* of them differs from the
  // baseline — gating on the workflow alone hides real changes when the
  // user has only edited connections (or parameters/notes).
  const liveWorkflow = useWorkflowStore((s) => s.workflow);
  const prodWorkflow = useDraftModeStore((s) => s.prodWorkflow);
  const prodConnections = useDraftModeStore((s) => s.prodConnections);
  const prodParameters = useDraftModeStore((s) => s.prodParameters);
  const prodNotes = useDraftModeStore((s) => s.prodNotes);
  // Parameters and notes have no separate "live" store — while in draft
  // mode the draft* slices on draftModeStore are the live state.
  const liveParameters = useDraftModeStore((s) => s.draftParameters);
  const liveNotes = useDraftModeStore((s) => s.draftNotes);
  // Subscribe to the inputs to `exportToConnectionsConfig` so the live
  // connections memo below recomputes on every connection edit. Reading
  // the function directly via `useConnectionStore((s) => s.export...())`
  // would return a fresh object each render and thrash this component.
  const connectionReferences = useConnectionStore((s) => s.references);
  const connectionMapping = useConnectionStore((s) => s.mapping);
  const connectionOriginalConfig = useConnectionStore((s) => s.originalConnectionsConfig);
  const sessionStart = useSessionHistoryStore((s) => s.history[0]);
  const sessionStartWorkflow = sessionStart?.workflow;

  // Build the live connections config from the connection store. This is
  // the exact shape the save path serializes, so it's also what a discard
  // would replace — comparing it against the baselines tells us truthfully
  // whether discarding would change anything.
  const liveConnections = useMemo(
    () => useConnectionStore.getState().exportToConnectionsConfig(),
    // The exhaustive-deps lint can't see that exportToConnectionsConfig
    // reads `references` / `mapping` / `originalConnectionsConfig` via
    // `getState()` — we depend on the subscribed slices above precisely so
    // this memo recomputes when any of those change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [connectionReferences, connectionMapping, connectionOriginalConfig],
  );

  // Each discard action is enabled iff the live canvas differs in *content*
  // from its baseline. We can't use identity here because `setWorkflowFiles`
  // runs `normalizeWorkflow` which clones, and the stored `draftWorkflow`
  // snapshot lags behind unsaved edits — both would yield false positives
  // ("dirty" forever) or false negatives ("clean" while edits are pending).
  // Comparing the live canvas directly matches the user's mental model:
  // "there's something to discard iff what I see differs from the baseline".
  const canDiscardDraft = useMemo(() => {
    if (hasContentDiff(liveWorkflow, prodWorkflow)) return true;
    if (
      hasArtifactDiff(
        normalizeConnections(liveConnections),
        normalizeConnections(prodConnections),
      )
    )
      return true;
    if (hasArtifactDiff(liveParameters, prodParameters)) return true;
    if (hasArtifactDiff(liveNotes, prodNotes)) return true;
    return false;
  }, [
    liveWorkflow,
    prodWorkflow,
    liveConnections,
    prodConnections,
    liveParameters,
    prodParameters,
    liveNotes,
    prodNotes,
  ]);
  const canDiscardSession = useMemo(() => {
    if (!sessionStart) return false;
    if (hasContentDiff(liveWorkflow, sessionStartWorkflow)) return true;
    if (
      hasArtifactDiff(
        normalizeConnections(liveConnections),
        normalizeConnections(sessionStart.connections ?? null),
      )
    )
      return true;
    if (hasArtifactDiff(liveParameters, sessionStart.parameters ?? null)) return true;
    if (hasArtifactDiff(liveNotes, sessionStart.notes ?? null)) return true;
    return false;
  }, [
    liveWorkflow,
    sessionStartWorkflow,
    liveConnections,
    sessionStart,
    liveParameters,
    liveNotes,
  ]);

  // Both discard actions are destructive and irreversible, so we route
  // them through a confirmation dialog instead of firing on first click.
  // `confirmKind` doubles as the dialog’s open flag and content selector.
  const [confirmKind, setConfirmKind] = useState<DiscardConfirmKind | null>(null);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [isConnectionsOpen, setIsConnectionsOpen] = useState(false);
  const closeConfirm = useCallback(() => {
    // Don't allow dismiss while the discard is in flight — otherwise the
    // user could close the dialog mid-API-call and assume nothing happened.
    if (isDiscarding) return;
    setConfirmKind(null);
  }, [isDiscarding]);
  const handleConfirmDiscard = useCallback(async () => {
    if (!confirmKind) return;
    const copy = DISCARD_CONFIRM_COPY[confirmKind];
    telemetryService.trackEvent(copy.telemetry);
    setIsDiscarding(true);
    try {
      if (confirmKind === 'session') {
        await discardSession();
      } else {
        await discardDraft();
      }
    } finally {
      // Reset both flags together so the dialog can’t flash a “Confirm”
      // state after an in-flight discard finishes.
      setIsDiscarding(false);
      setConfirmKind(null);
    }
  }, [confirmKind, discardSession, discardDraft]);

  // Mode switch goes through the shared guard hook so unsaved draft edits
  // can't be silently dropped when flipping into the read-only published
  // view. `switchToDraft` is unguarded — published is read-only, nothing
  // to protect on that direction.
  const modeSwitch = useGuardedModeSwitch({
    ...(workflowName && { workflowName }),
  });
  const handleSwitchMode = useCallback(() => {
    if (isDraftMode) {
      modeSwitch.requestSwitchToPublished();
    } else {
      modeSwitch.switchToDraft();
    }
  }, [isDraftMode, modeSwitch]);

  const confirmCopy = confirmKind ? DISCARD_CONFIRM_COPY[confirmKind] : null;

  return (
    <>
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Tooltip content="More actions" relationship="description">
          <Button
            appearance="subtle"
            icon={<MoreVertical20Regular />}
            aria-label="More workflow actions"
          />
        </Tooltip>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem
            icon={<ArrowSwap20Regular />}
            onClick={handleSwitchMode}
          >
            {isDraftMode ? 'View published version' : 'Edit draft'}
          </MenuItem>

          <MenuDivider />
          <MenuItem
            icon={<PlugConnected20Regular />}
            // Deferred to a microtask so the menu's close transition commits before
            // the dialog mounts
            onClick={() => queueMicrotask(() => setIsConnectionsOpen(true))}
          >
            Connections
          </MenuItem>

          {/* Validate workflow — only meaningful while editing the draft */}
          {isDraftMode && onValidate && (
            <>
              <MenuDivider />
              <MenuItem
                icon={isValidating ? <Spinner size="tiny" /> : <CheckmarkCircle20Regular />}
                onClick={onValidate}
                disabled={isValidating}
              >
                {buildValidateLabel(isValidating, validationErrorCount)}
              </MenuItem>
            </>
          )}

          {/* Discard actions — only while editing the draft.
              The `setConfirmKind` calls are deferred via `queueMicrotask` so the
              Menu's tabster modalizer finishes tearing down before the Dialog's
              modalizer registers. Without the defer, the two modalizers overlap
              in the same React commit and tabster marks the freshly-mounted
              dialog `aria-hidden="true"` (trapping focus on the closing menu),
              which breaks screen readers, axe scans, and Playwright role queries. */}
          {isDraftMode && (
            <>
              <MenuDivider />
              <MenuItem
                icon={<ArrowReset20Regular />}
                onClick={() => queueMicrotask(() => setConfirmKind('session'))}
                disabled={!canDiscardSession}
              >
                Discard session
              </MenuItem>
              <MenuItem
                className={styles.destructiveMenuItem}
                icon={<Delete20Regular />}
                onClick={() => queueMicrotask(() => setConfirmKind('draft'))}
                disabled={!canDiscardDraft}
              >
                Discard draft
              </MenuItem>
            </>
          )}
        </MenuList>
      </MenuPopover>
    </Menu>

    {/*
      Confirmation dialog for both discard actions. Rendered outside the
      Menu so that clicking the menu item (which closes the popover) still
      leaves the dialog mounted. We keep a single Dialog instance and
      swap the copy via `confirmKind` — simpler than two parallel dialogs
      and guarantees only one is ever open.
    */}
    <Dialog
      open={confirmKind !== null}
      onOpenChange={(_, data) => {
        if (!data.open) closeConfirm();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{confirmCopy?.title}</DialogTitle>
          <DialogContent>{confirmCopy?.description}</DialogContent>
          <DialogActions>
            <Button
              appearance="secondary"
              onClick={closeConfirm}
              disabled={isDiscarding}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              className={styles.destructiveConfirmButton}
              onClick={() => { void handleConfirmDiscard(); }}
              disabled={isDiscarding}
              {...(isDiscarding ? { icon: <Spinner size="tiny" /> } : {})}
            >
              {isDiscarding ? (confirmCopy?.busyLabel ?? 'Discarding…') : (confirmCopy?.confirmLabel ?? 'Discard')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>

    {/*
      Unsaved-changes guard for switching draft → published. Rendered as
      a sibling to the discard dialog (only one is ever open at a time).
      The hook owns its own open/saving/error state — we just mount the
      node it returns.
    */}
    {modeSwitch.dialog}

    {/* Connections configuration — view/change the connections this workflow
        uses. Rendered outside the Menu so closing the popover leaves it open. */}
    {isConnectionsOpen && (
      <ConnectionsDialog
        open={isConnectionsOpen}
        onClose={() => setIsConnectionsOpen(false)}
      />
    )}
    </>
  );
};
